import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export default function BillingPricing({ onAllSubscriptionData }) {
  const navigate = useNavigate();

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [allsubscriptionData, setAllSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Make a GET request to fetch current subscription information
    fetch("/api/current-subscription")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        // Update state with subscription information
        setSubscriptionData(data);
        setAllSubscriptionData(data.allSubscriptions);
        // Update userRole based on subscription data
        onAllSubscriptionData(data.allSubscriptions);

        setUserRole(data.userRole);
      })
      .catch((error) => {
        setError(error.message);
      });
  }, [onAllSubscriptionData]);

  const handleCancelSubscription = () => {
    confirmAlert({
      title: "Confirm to proceed",
      message: "Are you sure you want to cancel the current plan?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            fetch("/api/cancel-subscription", {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({}),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Failed to cancel subscription");
                }
                return response.json();
              })
              .then((data) => {
                // Handle successful cancellation, e.g., update state or navigate
                setSubscriptionData(null); // Clear the current subscription data
              })
              .catch((error) => {
                console.error("Error canceling subscription:", error);
                // Handle the error appropriately, e.g., displaying an error message to the user
              });
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleUpdateSubscription = (plan) => {
    confirmAlert({
      title: "Confirm to proceed",
      message: `Are you sure you want to change the plan to ${plan.planName}?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            const { planId, duration, planName, price } = plan;
            const requestBody = {
              planId,
              duration,
              plan: plan.planName, // Rename planName to plan
              price,
            };
            fetch("/api/update-subscription", {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestBody),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Failed to update subscription");
                }
                return response.json();
              })
              .then((data) => {
                // If the subscription update is successful, you may want to navigate the user to another page
                setSelectedPlan(plan);
                navigate("/billing");
              })
              .catch((error) => {
                console.error("Error updating subscription:", error);
                // Handle the error appropriately, e.g., displaying an error message to the user
              });
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!subscriptionData) {
    return <div>Loading...</div>;
  }
  console.log("subscriptionData", subscriptionData);
  const plans = [
    {
      planName: "Basic",
      price: 30,
      duration: 1,
      features: [
        "1 Month Access",
        "AI-Powered Learning",
        "AI-Assisted Modules",
        "Unlimited Mock",
      ],
      planId: "price_1PLrtNDPGZKo4YmHNBxK2InW",
    },
    {
      planName: "Starter",
      price: 20,
      duration: 3,
      features: [
        "3 Months Access",
        "AI-Powered Learning",
        "AI-Assisted Modules",
        "Unlimited Mock",
      ],
      planId: "price_1PLrp2DPGZKo4YmHvV1DHZNY",
    },
    {
      planName: "Premium",
      price: 15,
      duration: 6,
      features: [
        "6 Months Access",
        "AI-Powered Learning",
        "AI-Assisted Modules",
        "Unlimited Mock",
      ],
      planId: "price_1PLrtNDPGZKo4YmHNBxK2InW",
    },
  ];

  return (
    <div className=" mx-10 lg:ml-80 max-w-5xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 bg-slate-50">
      <div className="mx-auto grid grid-cols-1 gap-6 sm:grid-cols-3 sm:items-center md:gap-8">
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`relative rounded-lg border p-6 shadow-md cursor-pointer ${
              subscriptionData.currentPlan === plan.planName
                ? "border-fuchsia-700"
                : "border-gray-200"
            }`}
            onClick={() => setSelectedPlan(plan)}
          >
            {subscriptionData.currentPlan === plan.planName && (
              <div className="absolute top-0 right-0 bg-fuchsia-700 text-white px-2 py-1 rounded-tr-lg rounded-bl-lg text-xs font-semibold">
                Your Plan
              </div>
            )}
            <div className="text-center">
              <h2 className="text-lg font-medium text-gray-900">
                {plan.planName}
              </h2>
              <p className="mt-2 sm:mt-4">
                <strong className="text-3xl font-bold text-gray-900 sm:text-4xl">
                  {plan.price}$
                </strong>
                <span className="text-sm font-medium text-gray-700">
                  /month
                </span>
              </p>
            </div>
            <ul className="mt-6 space-y-2">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="h-5 w-5 text-indigo-700"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            {subscriptionData &&
            subscriptionData.subscriptionStatus === "Active" &&
            subscriptionData.currentPlan ? (
              subscriptionData.currentPlan === plan.planName ? (
                userRole !== 7 && (
                  <button
                    className="mt-8 block mx-auto rounded-full border bg-gray-900 px-12 py-3 text-center text-sm font-medium text-gray-200 hover:ring-1 hover:ring-purple-500 bg-gradient-to-r from-fuchsia-900 to-fuchsia-700"
                    onClick={handleCancelSubscription}
                  >
                    Cancel
                  </button>
                )
              ) : (
                <button
                  className="mt-8 block mx-auto rounded-full border bg-gray-900 px-12 py-3 text-center text-sm font-medium text-gray-200 hover:ring-1 hover:ring-purple-500 bg-gradient-to-r from-fuchsia-900 to-fuchsia-700"
                  onClick={() => handleUpdateSubscription(plan)}
                >
                  Change
                </button>
              )
            ) : (
              <button
                className="mt-8 block mx-auto rounded-full border bg-gray-900 px-12 py-3 text-center text-sm font-medium text-gray-200 hover:ring-1 hover:ring-purple-500 bg-gradient-to-r from-fuchsia-900 to-fuchsia-700"
                onClick={() =>
                  navigate("/billingcheckout", { state: { plan: plan } })
                }
              >
                Buy
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
